import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConfiguratorPage = () => {
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState(null);
  const [configuration, setConfiguration] = useState({
    belt_type: '',
    thickness: '',
    width: '',
    length: '',
    number_of_plies: '',
    colour: '',
    surface_type: '',
    temperature_range: '',
    oil_chemical_resistance: false,
    quantity: ''
  });

  const [rfqData, setRfqData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    gst_number: '',
    delivery_location: '',
    required_delivery_date: '',
    additional_remarks: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCatalog();
  }, []);

  const fetchCatalog = async () => {
    try {
      const response = await axios.get(`${API}/product-catalog`);
      setCatalog(response.data);
    } catch (error) {
      console.error('Error fetching catalog:', error);
      toast.error('Failed to load product catalog');
    }
  };

  const handleConfigChange = (field, value) => {
    setConfiguration(prev => ({ ...prev, [field]: value }));
  };

  const handleRfqChange = (field, value) => {
    setRfqData(prev => ({ ...prev, [field]: value }));
  };

  const validateConfiguration = () => {
    const required = ['belt_type', 'thickness', 'width', 'length', 'number_of_plies', 'colour', 'surface_type', 'temperature_range', 'quantity'];
    for (let field of required) {
      if (!configuration[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    return true;
  };

  const validateRfq = () => {
    const required = ['company_name', 'contact_person', 'email', 'phone', 'delivery_location', 'required_delivery_date'];
    for (let field of required) {
      if (!rfqData[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateConfiguration()) {
      setStep(2);
    }
  };

  const handleSubmitRfq = async () => {
    if (!validateRfq()) return;

    setLoading(true);
    try {
      const payload = {
        configuration: {
          ...configuration,
          thickness: parseFloat(configuration.thickness),
          width: parseFloat(configuration.width),
          length: parseFloat(configuration.length),
          number_of_plies: parseInt(configuration.number_of_plies),
          quantity: parseInt(configuration.quantity)
        },
        ...rfqData
      };

      await axios.post(`${API}/rfq`, payload);
      toast.success('RFQ submitted successfully! We will contact you soon.');
      
      // Reset form
      setStep(1);
      setConfiguration({
        belt_type: '',
        thickness: '',
        width: '',
        length: '',
        number_of_plies: '',
        colour: '',
        surface_type: '',
        temperature_range: '',
        oil_chemical_resistance: false,
        quantity: ''
      });
      setRfqData({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        gst_number: '',
        delivery_location: '',
        required_delivery_date: '',
        additional_remarks: ''
      });
    } catch (error) {
      console.error('Error submitting RFQ:', error);
      toast.error('Failed to submit RFQ. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#1E293B]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <div className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <h1 className="heading-font text-4xl text-[#1E293B] mb-4" data-testid="configurator-title">
              BELT CONFIGURATOR
            </h1>
            <p className="text-[#64748B] text-base">
              Configure your custom belt and submit an RFQ
            </p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 1 ? 'bg-[#3B82F6]' : 'bg-[#1E293B]'} text-[#1E293B] font-bold`} data-testid="step-1-indicator">
                1
              </div>
              <div className={`w-24 h-1 ${step >= 2 ? 'bg-[#3B82F6]' : 'bg-[#1E293B]'}`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 2 ? 'bg-[#3B82F6]' : 'bg-[#1E293B]'} text-[#1E293B] font-bold`} data-testid="step-2-indicator">
                2
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Configuration Form */}
            <div className="lg:col-span-2">
              {step === 1 ? (
                <div className="glass-card p-8" data-testid="configuration-form">
                  <h2 className="heading-font text-2xl text-[#1E293B] mb-6">STEP 1: CONFIGURE BELT</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="belt_type" className="text-[#1E293B] mb-2 block">Belt Type *</Label>
                      <Select value={configuration.belt_type} onValueChange={(val) => handleConfigChange('belt_type', val)}>
                        <SelectTrigger id="belt_type" data-testid="belt-type-select" className="bg-white border-gray-300 text-[#1E293B]">
                          <SelectValue placeholder="Select belt type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-300 text-[#1E293B]">
                          {catalog.belt_types.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="thickness" className="text-[#1E293B] mb-2 block">Thickness (mm) *</Label>
                      <Input
                        id="thickness"
                        type="number"
                        step="0.1"
                        data-testid="thickness-input"
                        placeholder="e.g., 5.0"
                        value={configuration.thickness}
                        onChange={(e) => handleConfigChange('thickness', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="width" className="text-[#1E293B] mb-2 block">Width (mm) *</Label>
                      <Input
                        id="width"
                        type="number"
                        step="0.1"
                        data-testid="width-input"
                        placeholder="e.g., 100.0"
                        value={configuration.width}
                        onChange={(e) => handleConfigChange('width', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="length" className="text-[#1E293B] mb-2 block">Length (mm) *</Label>
                      <Input
                        id="length"
                        type="number"
                        step="0.1"
                        data-testid="length-input"
                        placeholder="e.g., 1000.0"
                        value={configuration.length}
                        onChange={(e) => handleConfigChange('length', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="number_of_plies" className="text-[#1E293B] mb-2 block">Number of Plies *</Label>
                      <Input
                        id="number_of_plies"
                        type="number"
                        data-testid="plies-input"
                        placeholder="e.g., 2"
                        value={configuration.number_of_plies}
                        onChange={(e) => handleConfigChange('number_of_plies', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="colour" className="text-[#1E293B] mb-2 block">Colour *</Label>
                      <Select value={configuration.colour} onValueChange={(val) => handleConfigChange('colour', val)}>
                        <SelectTrigger id="colour" data-testid="colour-select" className="bg-white border-gray-300 text-[#1E293B]">
                          <SelectValue placeholder="Select colour" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-300 text-[#1E293B]">
                          {catalog.colours.map((colour) => (
                            <SelectItem key={colour} value={colour}>{colour}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="surface_type" className="text-[#1E293B] mb-2 block">Surface Type *</Label>
                      <Select value={configuration.surface_type} onValueChange={(val) => handleConfigChange('surface_type', val)}>
                        <SelectTrigger id="surface_type" data-testid="surface-type-select" className="bg-white border-gray-300 text-[#1E293B]">
                          <SelectValue placeholder="Select surface type" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-300 text-[#1E293B]">
                          {catalog.surface_types.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="temperature_range" className="text-[#1E293B] mb-2 block">Temperature Range *</Label>
                      <Select value={configuration.temperature_range} onValueChange={(val) => handleConfigChange('temperature_range', val)}>
                        <SelectTrigger id="temperature_range" data-testid="temperature-select" className="bg-white border-gray-300 text-[#1E293B]">
                          <SelectValue placeholder="Select temperature range" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-300 text-[#1E293B]">
                          {catalog.temperature_ranges.map((range) => (
                            <SelectItem key={range} value={range}>{range}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="quantity" className="text-[#1E293B] mb-2 block">Quantity *</Label>
                      <Input
                        id="quantity"
                        type="number"
                        data-testid="quantity-input"
                        placeholder="e.g., 10"
                        value={configuration.quantity}
                        onChange={(e) => handleConfigChange('quantity', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div className="flex items-center space-x-3">
                      <Switch
                        id="oil_chemical_resistance"
                        data-testid="resistance-switch"
                        checked={configuration.oil_chemical_resistance}
                        onCheckedChange={(checked) => handleConfigChange('oil_chemical_resistance', checked)}
                      />
                      <Label htmlFor="oil_chemical_resistance" className="text-[#1E293B]">
                        Oil/Chemical Resistance
                      </Label>
                    </div>
                  </div>

                  <div className="mt-8">
                    <Button
                      onClick={handleNext}
                      className="btn-accent w-full md:w-auto px-12 py-6"
                      data-testid="next-step-btn"
                    >
                      Next: Enter Company Details
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="glass-card p-8" data-testid="rfq-form">
                  <h2 className="heading-font text-2xl text-[#1E293B] mb-6">STEP 2: COMPANY DETAILS</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company_name" className="text-[#1E293B] mb-2 block">Company Name *</Label>
                      <Input
                        id="company_name"
                        data-testid="company-name-input"
                        placeholder="Enter company name"
                        value={rfqData.company_name}
                        onChange={(e) => handleRfqChange('company_name', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact_person" className="text-[#1E293B] mb-2 block">Contact Person *</Label>
                      <Input
                        id="contact_person"
                        data-testid="contact-person-input"
                        placeholder="Enter contact person name"
                        value={rfqData.contact_person}
                        onChange={(e) => handleRfqChange('contact_person', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#1E293B] mb-2 block">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="email-input"
                        placeholder="email@company.com"
                        value={rfqData.email}
                        onChange={(e) => handleRfqChange('email', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-[#1E293B] mb-2 block">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="phone-input"
                        placeholder="+91 1234567890"
                        value={rfqData.phone}
                        onChange={(e) => handleRfqChange('phone', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="gst_number" className="text-[#1E293B] mb-2 block">GST Number</Label>
                      <Input
                        id="gst_number"
                        data-testid="gst-input"
                        placeholder="Enter GST number"
                        value={rfqData.gst_number}
                        onChange={(e) => handleRfqChange('gst_number', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="delivery_location" className="text-[#1E293B] mb-2 block">Delivery Location *</Label>
                      <Input
                        id="delivery_location"
                        data-testid="delivery-location-input"
                        placeholder="Enter delivery location"
                        value={rfqData.delivery_location}
                        onChange={(e) => handleRfqChange('delivery_location', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="required_delivery_date" className="text-[#1E293B] mb-2 block">Required Delivery Date *</Label>
                      <Input
                        id="required_delivery_date"
                        type="date"
                        data-testid="delivery-date-input"
                        value={rfqData.required_delivery_date}
                        onChange={(e) => handleRfqChange('required_delivery_date', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Label htmlFor="additional_remarks" className="text-[#1E293B] mb-2 block">Additional Remarks</Label>
                      <Textarea
                        id="additional_remarks"
                        data-testid="remarks-textarea"
                        placeholder="Enter any additional requirements or remarks"
                        value={rfqData.additional_remarks}
                        onChange={(e) => handleRfqChange('additional_remarks', e.target.value)}
                        className="bg-white border-gray-300 text-[#1E293B] h-24"
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex gap-4">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="btn-secondary px-8 py-6"
                      data-testid="back-btn"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmitRfq}
                      disabled={loading}
                      className="btn-accent px-12 py-6"
                      data-testid="submit-rfq-btn"
                    >
                      {loading ? 'Submitting...' : 'Submit RFQ'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-1">
              <div className="glass-card p-6 sticky top-24" data-testid="configuration-preview">
                <h3 className="heading-font text-xl text-[#1E293B] mb-4">CONFIGURATION PREVIEW</h3>
                <div className="space-y-3">
                  <div className="pb-3 border-b border-white/10">
                    <div className="text-[#64748B] text-xs mb-1">Belt Type</div>
                    <div className="text-[#1E293B] mono-font text-sm">{configuration.belt_type || '-'}</div>
                  </div>
                  <div className="pb-3 border-b border-white/10">
                    <div className="text-[#64748B] text-xs mb-1">Dimensions</div>
                    <div className="text-[#1E293B] mono-font text-sm">
                      {configuration.thickness && configuration.width && configuration.length
                        ? `${configuration.thickness} × ${configuration.width} × ${configuration.length} mm`
                        : '-'}
                    </div>
                  </div>
                  <div className="pb-3 border-b border-white/10">
                    <div className="text-[#64748B] text-xs mb-1">Plies</div>
                    <div className="text-[#1E293B] mono-font text-sm">{configuration.number_of_plies || '-'}</div>
                  </div>
                  <div className="pb-3 border-b border-white/10">
                    <div className="text-[#64748B] text-xs mb-1">Colour</div>
                    <div className="text-[#1E293B] mono-font text-sm">{configuration.colour || '-'}</div>
                  </div>
                  <div className="pb-3 border-b border-white/10">
                    <div className="text-[#64748B] text-xs mb-1">Surface Type</div>
                    <div className="text-[#1E293B] mono-font text-sm">{configuration.surface_type || '-'}</div>
                  </div>
                  <div className="pb-3 border-b border-white/10">
                    <div className="text-[#64748B] text-xs mb-1">Temperature Range</div>
                    <div className="text-[#1E293B] mono-font text-sm">{configuration.temperature_range || '-'}</div>
                  </div>
                  <div className="pb-3 border-b border-white/10">
                    <div className="text-[#64748B] text-xs mb-1">Oil/Chemical Resistance</div>
                    <div className="text-[#1E293B] mono-font text-sm">
                      {configuration.oil_chemical_resistance ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <div className="pb-3">
                    <div className="text-[#64748B] text-xs mb-1">Quantity</div>
                    <div className="text-[#1E293B] mono-font text-sm">{configuration.quantity || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConfiguratorPage;
